import { useMemo, useState } from 'react'
import { Icon } from './Icon'

/**
 * The app's own month calendar, themed and shared: the reminder editor's date step and the Contexts date question both use
 * it, so a date is chosen the same way everywhere. Dates are `YYYY-MM-DD` in the device's own calendar. It keeps its month
 * (starting from the date it is given) and says what was chosen; the caller decides when the choice is final.
 */
export type CalendarWeekStart = 'monday' | 'sunday'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export function calendarMonthFor(value: string): Date {
  const [year = 0, month = 0] = value.split('-').map(Number)
  return Number.isFinite(year) && Number.isFinite(month) && year > 0 ? new Date(year, month - 1, 1) : new Date()
}

export function calendarDateValue(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

/** The days of a month as grid cells: blanks up to the first weekday, then each day. */
export function calendarCells(month: Date, weekStartsOn: CalendarWeekStart): (Date | null)[] {
  const weekStartDay = weekStartsOn === 'sunday' ? 0 : 1
  const blanks = (month.getDay() - weekStartDay + 7) % 7
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  return Array.from({ length: blanks + daysInMonth }, (_, index) => index < blanks ? null : new Date(month.getFullYear(), month.getMonth(), index - blanks + 1))
}

export function CalendarPicker({ value, min, weekStartsOn, label = 'Choose date', confirmLabel = 'Set date', onChange, onConfirm }: {
  value: string
  /** Days before this one cannot be chosen. */
  min?: string
  weekStartsOn: CalendarWeekStart
  label?: string
  confirmLabel?: string
  onChange: (date: string) => void
  onConfirm: () => void
}) {
  const [month, setMonth] = useState(() => calendarMonthFor(value))
  const today = calendarDateValue(new Date())
  const weekdays = useMemo(() => weekStartsOn === 'sunday' ? WEEKDAY_LABELS : [...WEEKDAY_LABELS.slice(1), WEEKDAY_LABELS[0]!], [weekStartsOn])
  const cells = useMemo(() => calendarCells(month, weekStartsOn), [month, weekStartsOn])
  const step = (by: number) => setMonth(current => new Date(current.getFullYear(), current.getMonth() + by, 1))
  return <section className="reminder-editor__date-picker" aria-label={label}>
    <div className="reminder-editor__calendar-heading">
      <button className="btn btn-ghost btn-square" type="button" onClick={() => step(-1)} aria-label="Previous month"><Icon name="chevron" /></button>
      <strong>{new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(month)}</strong>
      <button className="btn btn-ghost btn-square reminder-editor__calendar-next" type="button" onClick={() => step(1)} aria-label="Next month"><Icon name="chevron" /></button>
    </div>
    <div className="reminder-editor__calendar-grid" role="grid" aria-label="Calendar">
      {weekdays.map(weekday => <span className="reminder-editor__calendar-weekday" key={weekday}>{weekday}</span>)}
      {cells.map((day, index) => {
        if (!day) return <span key={`empty-${index}`} aria-hidden="true" />
        const dayValue = calendarDateValue(day)
        return <button key={dayValue} className={`btn reminder-editor__calendar-day${dayValue === value ? ' is-selected' : ''}${dayValue === today ? ' is-today' : ''}`} type="button"
          disabled={min !== undefined && dayValue < min} onClick={() => onChange(dayValue)} aria-pressed={dayValue === value} aria-current={dayValue === today ? 'date' : undefined}>{day.getDate()}</button>
      })}
    </div>
    <div className="reminder-editor__date-actions">
      <button className="btn" type="button" disabled={min !== undefined && today < min}
        onClick={() => { setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); onChange(today) }}>Today</button>
      <button className="btn btn-primary" type="button" onClick={onConfirm}>{confirmLabel}</button>
    </div>
  </section>
}
